import React, { useState, Fragment } from "react";
import { ListBuilder, TileGrid, FormBuilder, Form } from "@redriver/cinnamon";
import { GroupSection, HeaderBar } from "components/containers";
import { PermissionCheck } from "components/auth";
import { Targets, Actions } from "constants/Permissions";
import { addContractDocument, getContractDocuments } from "./actions";
import { ContractDocumentCategory } from "constants/enums";
import { Button, ButtonGroup, Container } from "semantic-ui-react";
import { DocumentTypesDropdown } from "components/forms";
import { AddDocumentsModal } from "components/modals";
import TablePagination from "components/pagination/TablePagination";
import ContractDocumentTableActions from "./ContractDocumentTableActions";

const ContractDocumentsPanel = ({ contractId, departmentId }) => {
  const [documentCategory, setDocumentCategory] = useState(
    ContractDocumentCategory.Contract,
  );

  return (
    <ListBuilder
      withTiles
      loadAction={getContractDocuments}
      loadParams={{ contractId, category: documentCategory }}
      dataTransform={(data) => {
        const items = data.results || [];
        const byType = items.reduce((acc, x) => {
          const key = x.documentType.name || "unknown";
          acc[key] = acc[key] || [];
          acc[key].push(x);
          return acc;
        }, {});
        const groups = Object.entries(byType).map(([documentType, items]) => ({
          documentType,
          items,
        }));
        return groups;
      }}
      initialPagination={{ pageSize: 25 }}
      totalTransform={(data) => data.totalResults}
      renderList={(tileProps, state, events) => {
        const { pageNumber, pageSize } = state.pagination;
        return (
          <Fragment>
            <Container className="documents-panel" fluid>
              <HeaderBar
                isSearchBar
                name={
                  <ButtonGroup className="header-button-group">
                    <Button
                      className={
                        documentCategory === ContractDocumentCategory.Contract
                          ? "selected"
                          : ""
                      }
                      onClick={() => {
                        setDocumentCategory(ContractDocumentCategory.Contract);
                        events.onRefresh();
                      }}
                    >
                      Contract
                    </Button>
                    <Button
                      className={
                        documentCategory === ContractDocumentCategory.Sales
                          ? "selected"
                          : ""
                      }
                      onClick={() => {
                        setDocumentCategory(ContractDocumentCategory.Sales);
                        events.onRefresh();
                      }}
                    >
                      Sales
                    </Button>
                  </ButtonGroup>
                }
                rightContent={
                  <PermissionCheck
                    target={Targets.ContractDocument}
                    action={Actions.Create}
                  >
                    <AddDocumentsModal
                      header="Add Contract Document"
                      submitAction={addContractDocument}
                      submitParams={{ contractId, category: documentCategory }}
                      onAdded={events.onRefresh}
                      documentTypeDropdown={
                        <DocumentTypesDropdown
                          field="type"
                          label="Type"
                          placeholder="Select document type"
                          departmentId={departmentId}
                          required
                          fluid
                        />
                      }
                    />
                  </PermissionCheck>
                }
              >
                <FormBuilder
                  value={state.filters}
                  onChange={events.onChangeFilters}
                  renderForm={(formProps) => (
                    <Form className="list-filters" {...formProps}>
                      <Form.Group widths="equal">
                        <Form.Input
                          field="search"
                          placeholder="Search"
                          icon="search"
                          fluid
                        />
                        {departmentId ? (
                          <DocumentTypesDropdown
                            className="type"
                            field="documentTypeId"
                            placeholder="Type"
                            departmentId={departmentId}
                            clearable
                          />
                        ) : null}
                      </Form.Group>
                    </Form>
                  )}
                />
              </HeaderBar>

              <TileGrid
                {...tileProps}
                dataKey="id"
                render={(item) => (
                  <GroupSection
                    group={item}
                    Id={contractId}
                    typeActions={(document) => (
                      <ContractDocumentTableActions
                        document={document}
                        departmentId={departmentId}
                        contractId={contractId}
                        onRefresh={events.onRefresh}
                      />
                    )}
                    onRefresh={events.onRefresh}
                  />
                )}
              />
            </Container>

            <TablePagination
              pageNumber={pageNumber}
              pageSize={pageSize}
              withPageSize
              pageSizeOptions={[25, 50, 100]}
              onChange={(changes) =>
                events.onChangePagination({
                  ...state.pagination,
                  ...changes,
                })
              }
              totalItems={state.total}
            />
          </Fragment>
        );
      }}
    />
  );
};

export default ContractDocumentsPanel;
